import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { boardApi } from "../api/client.js";
import { useToast } from "../components/Toast.jsx";

/* The board, and the one place that writes it.
 *
 * Nothing is written until somebody presses Save. An edit lands in local
 * state immediately, so typing feels like typing, and the changed fields
 * queue up per playground until its drawer's Save button flushes them. A
 * refresh that arrives while a document has unsaved edits leaves that
 * document alone, so the queue is never clobbered by someone else's poll —
 * which matters far more now that a document can sit unsaved for as long
 * as its author likes.
 *
 * Writes are last-writer-wins across people, as they were before. */

const BoardContext = createContext(null);
const POLL_MS = 30000;

export function BoardProvider({ children }) {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(0);
  /* Which playgrounds are queued but not yet written. `pending` is a ref so
     that typing does not re-render the board, which leaves nothing for a
     Save button to read — so the same fact is mirrored into state, and
     every place that touches `pending` says so here. It is the ids rather
     than a flag because each playground's drawer saves itself, and its
     button has to speak for that one document. */
  const [dirtyIds, setDirtyIds] = useState([]);
  const toast = useToast();

  const pending = useRef({});
  /* The latest board, so an edit can be computed without depending on it —
     a state updater must not have side effects, and queueing the patch is
     one. */
  const latest = useRef(null);
  latest.current = board;

  /* Same ids, same array: a keystroke on an already-queued playground
     should not re-render every drawer that is reading this. */
  const syncDirty = useCallback(() => {
    const ids = Object.keys(pending.current);
    setDirtyIds((prev) =>
      prev.length === ids.length && prev.every((id) => ids.includes(id)) ? prev : ids
    );
  }, []);

  const load = useCallback(async () => {
    try {
      const { playgrounds } = await boardApi.list();
      setBoard((prev) => {
        if (!prev) return playgrounds;
        /* Keep any document with an unflushed edit; take the server's copy
           of everything else. */
        return playgrounds.map((fresh) =>
          pending.current[fresh.id]
            ? prev.find((p) => p.id === fresh.id) || fresh
            : fresh
        );
      });
      setError(null);
    } catch (e) {
      /* A 401 is handled globally by the auth layer; anything else is shown
         where the board would be. */
      if (e.status !== 401) setError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* One person's edit reaching another person's open tab was a live
     subscription in the artifact build. Without one, a poll every 30s is
     the honest substitute: it is not instant and does not pretend to be.
     Polling pauses while the tab is hidden. */
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) load();
    }, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const flush = useCallback(
    async (id) => {
      const patch = pending.current[id];
      if (!patch) return;
      delete pending.current[id];
      syncDirty();
      setSaving((n) => n + 1);
      try {
        const { playground } = await boardApi.patch(id, patch);
        /* Anything typed while the request was in flight is newer than the
           answer, and is already queued for the next flush — applying the
           server's copy here would snap the caret's field back. */
        if (!pending.current[id]) {
          setBoard((prev) =>
            prev ? prev.map((p) => (p.id === id ? { ...p, ...playground } : p)) : prev
          );
        }
      } catch (e) {
        if (e.status !== 401) toast(e.message, true);
        /* Put the edit back so a later flush retries it, rather than
           dropping the operator's typing on the floor. Anything queued
           since wins, being newer. */
        pending.current[id] = { ...patch, ...(pending.current[id] || {}) };
        syncDirty();
      } finally {
        setSaving((n) => n - 1);
      }
    },
    [toast, syncDirty]
  );

  /* With nothing saving in the background, closing the tab on unsaved work
     loses it outright — so the browser is asked to say so. It is the one
     place the application cannot offer its own confirmation, and the
     wording belongs to the browser. */
  useEffect(() => {
    if (!dirtyIds.length) return undefined;
    const onLeave = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirtyIds]);

  /* `edit(id, fn)` applies fn to a copy of the playground, shows it at once
     and queues the changed fields. It never writes — that is Save's job,
     and the only job it has. */
  const edit = useCallback(
    (id, fn) => {
      const current = latest.current?.find((p) => p.id === id);
      if (!current) return;
      const next = fn(structuredClone(current)) || current;
      pending.current[id] = { ...(pending.current[id] || {}), ...changed(current, next) };
      syncDirty();
      setBoard((prev) => (prev ? prev.map((p) => (p.id === id ? next : p)) : prev));
    },
    [syncDirty]
  );

  /* The only thing that writes an edit.
   *
   * `save(id)` writes one playground, which is what the drawer's button
   * calls; `save()` with nothing writes whatever is queued anywhere. Either
   * way the promise settles when the last document has landed, so a button
   * can stay busy until the write is real. A flush that fails re-queues its
   * edit and says so, so pressing Save again retries. */
  const save = useCallback(
    async (id) => {
      const ids = id
        ? pending.current[id]
          ? [id]
          : []
        : Object.keys(pending.current);
      await Promise.all(ids.map((k) => flush(k)));
      /* A failed flush re-queues its edit rather than throwing — it has
         already said what went wrong — so whether the write landed is read
         back off the queue, and a caller cannot report a save that did not
         happen. */
      return ids.every((k) => !pending.current[k]);
    },
    [flush]
  );

  /* An action the server owns outright (logging a meeting, clearing a
     manual health value, stamping a KPI review) goes straight there and
     its answer replaces the local copy.
   *
   * That answer is the whole document, so the queue is laid back over it
   * rather than flushed first: pressing "Log meeting" writes the meeting
   * and nothing else. Unsaved typing stays unsaved, stays on screen, and
   * still waits for Save — the button does what it says and no more. */
  const act = useCallback(
    async (id, call, successMessage) => {
      setSaving((n) => n + 1);
      try {
        const { playground } = await call();
        setBoard((prev) =>
          prev
            ? prev.map((p) =>
                p.id === id ? { ...playground, ...(pending.current[id] || {}) } : p
              )
            : prev
        );
        if (successMessage) toast(successMessage);
        return playground;
      } catch (e) {
        if (e.status !== 401) toast(e.message, true);
        throw e;
      } finally {
        setSaving((n) => n - 1);
      }
    },
    [toast]
  );

  /* Throw the queue away and take the server's copy back. Closing a drawer
     on unsaved edits offers this, so that "I did not save that" is a thing
     the board can actually honour — without it an abandoned edit lives on
     in local state, looking saved to the next person who opens the drawer
     and to nobody else. */
  const discard = useCallback(
    async (id) => {
      delete pending.current[id];
      syncDirty();
      await load();
    },
    [load, syncDirty]
  );

  /* Adding and removing a playground change how many there are, so neither
     goes through the queue — they are single calls whose answer is the
     board's new shape, and they take effect immediately rather than
     waiting for a Save. */
  const create = useCallback(async () => {
    setSaving((n) => n + 1);
    try {
      const { playground } = await boardApi.create();
      setBoard((prev) => (prev ? [...prev, playground] : [playground]));
      return playground;
    } catch (e) {
      if (e.status !== 401) toast(e.message, true);
      throw e;
    } finally {
      setSaving((n) => n - 1);
    }
  }, [toast]);

  const remove = useCallback(
    async (id) => {
      /* Drop any queued edit first: flushing it after the delete would
         recreate nothing and just report a 404 at the person. */
      delete pending.current[id];
      syncDirty();
      setSaving((n) => n + 1);
      try {
        await boardApi.remove(id);
        setBoard((prev) => (prev ? prev.filter((p) => p.id !== id) : prev));
      } catch (e) {
        if (e.status !== 401) toast(e.message, true);
        throw e;
      } finally {
        setSaving((n) => n - 1);
      }
    },
    [toast, syncDirty]
  );

  const value = useMemo(
    () => ({
      board,
      error,
      saving: saving > 0,
      dirty: dirtyIds.length > 0,
      isDirty: (id) => dirtyIds.includes(id),
      loading: board === null && !error,
      reload: load,
      edit,
      act,
      create,
      remove,
      save,
      discard,
      replace: setBoard,
    }),
    [board, error, saving, dirtyIds, load, edit, act, create, remove, save, discard]
  );

  return <BoardContext.Provider value={value}>{children}</BoardContext.Provider>;
}

export function useBoard() {
  const ctx = useContext(BoardContext);
  if (!ctx) throw new Error("useBoard must be used inside <BoardProvider>");
  return ctx;
}

/* Which writable fields differ, so a PATCH carries the change and not the
   whole document. */
const WRITABLE = [
  "name",
  "owner",
  "description",
  "cadence",
  "manualHealth",
  "kpis",
  "players",
  "tasks",
  "template",
];

function changed(before, after) {
  const out = {};
  for (const k of WRITABLE) {
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) out[k] = after[k];
  }
  return out;
}
